// Auto-generated lightweight class for PDMRequirement
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { PDMRequirementReadAPI, PDMRequirementWriteAPI, PDMRequirementReadAPISchema, PDMRequirementWriteAPISchema } from '../types/pdmrequirement';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, Derivation, HasMembers, HasRequirements, HasSubRequirements, Refinement, Satisfaction, Specializes } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { SysMLType } from '../../sysml/api/sysmltype';
import { RequirementDefinition } from '../../sysml/api/requirementdefinition';
import { ActionDefinition } from '../../sysml/api/actiondefinition';
import { PDMProduct } from './pdmproduct';



export interface PDMRequirement extends Partial<PDMRequirementReadAPI> {}
export class PDMRequirement extends NeoNodeBase<PDMRequirementReadAPI, PDMRequirementWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "PDMRequirement";
  static readonly readSchema = PDMRequirementReadAPISchema;
  static readonly writeSchema = PDMRequirementWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    general: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "out" },
    specific: { target: "SysMLType", cardinality: "many", isParent: false, relClass: "Specializes", direction: "in" },
    abstract_requirement: { target: "RequirementDefinition", cardinality: "one", isParent: false, relClass: "Refinement", direction: "in" },
    satisfying_actions: { target: "ActionDefinition", cardinality: "many", isParent: false, relClass: "Satisfaction", direction: "in" },
    derived_requirements: { target: "RequirementDefinition", cardinality: "many", isParent: false, relClass: "Derivation", direction: "out" },
    source_requirement: { target: "RequirementDefinition", cardinality: "one", isParent: false, relClass: "Derivation", direction: "in" },
    refined_requirements: { target: "RequirementDefinition", cardinality: "many", isParent: false, relClass: "Refinement", direction: "out" },
    sub_requirements: { target: "RequirementDefinition", cardinality: "many", isParent: false, relClass: "HasSubRequirements", direction: "out" },
    parent_requirement: { target: "RequirementDefinition", cardinality: "one", isParent: true, relClass: "HasSubRequirements", direction: "in" },
    owner: { target: "PDMProduct", cardinality: "one", isParent: true, relClass: "HasRequirements", direction: "in" },
  };

  protected get modelName(): string {
    return 'PDMRequirement';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return PDMRequirement.label;
  }


  constructor(data: Partial<PDMRequirementWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: PDMRequirementReadAPI, loadContext?: Record<string, any>): PDMRequirement {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { satisfying_actions, derived_requirements, source_requirement, refined_requirements, sub_requirements, parent_requirement, owner, security_scope, member_namespace, general, specific, abstract_requirement, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = PDMRequirementReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, satisfying_actions: satisfying_actions, derived_requirements: derived_requirements, source_requirement: source_requirement, refined_requirements: refined_requirements, sub_requirements: sub_requirements, parent_requirement: parent_requirement, owner: owner, security_scope: security_scope, member_namespace: member_namespace, general: general, specific: specific, abstract_requirement: abstract_requirement };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return PDMRequirement.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'satisfying_actions', 'ActionDefinition', ModelRegistry);
    loadManyRelationship(processed, 'derived_requirements', 'RequirementDefinition', ModelRegistry);
    loadManyRelationship(processed, 'source_requirement', 'RequirementDefinition', ModelRegistry);
    loadManyRelationship(processed, 'refined_requirements', 'RequirementDefinition', ModelRegistry);
    loadManyRelationship(processed, 'sub_requirements', 'RequirementDefinition', ModelRegistry);
    loadManyRelationship(processed, 'parent_requirement', 'RequirementDefinition', ModelRegistry);
    loadManyRelationship(processed, 'owner', 'PDMProduct', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadManyRelationship(processed, 'general', 'SysMLType', ModelRegistry);
    loadManyRelationship(processed, 'specific', 'SysMLType', ModelRegistry);
    loadOneRelationship(processed, 'abstract_requirement', 'RequirementDefinition', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new PDMRequirement(processed as any);

    return instance;
  }


  /**
   * Delete this instance from the server.
   *
   * @param options - Delete options
   * @param options.cascade - Whether to cascade delete related nodes (default: true for most models)
   * @param options.dryRun - If true, return what would be deleted without actually deleting
   * @returns DeleteResult with deleted IDs and count
   *
   * @example
   * ```typescript
   * const pdmrequirement = await PDMRequirement.get(id).first();
   * await pdmrequirement.delete();  // Delete with default cascade
   *
   * // Or with options
   * const result = await pdmrequirement.delete({ cascade: false, dryRun: true });
   * console.log('Would delete:', result.deletedIds);
   * ```
   */
  async delete(options: {
    cascade?: boolean;
    dryRun?: boolean;
  } = {}): Promise<{ deletedIds: string[]; count: number }> {
    return this._deleteInstance(options);
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'general' - Python ORM style (cached)
  get general(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('general', () => {
      return new RelationshipManager(this, 'general', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'specific' - Python ORM style (cached)
  get specific(): RelationshipManager<this, SysMLType> {
    return this.getOrCreateRelationshipManager('specific', () => {
      return new RelationshipManager(this, 'specific', 'many', Specializes as typeof Relationship);
    });
  }

  // Relationship manager for 'abstract_requirement' - Python ORM style (cached)
  get abstract_requirement(): RelationshipManager<this, RequirementDefinition> {
    return this.getOrCreateRelationshipManager('abstract_requirement', () => {
      return new RelationshipManager(this, 'abstract_requirement', 'one', Refinement as typeof Relationship);
    });
  }

  // Relationship manager for 'satisfying_actions' - Python ORM style (cached)
  get satisfying_actions(): RelationshipManager<this, ActionDefinition> {
    return this.getOrCreateRelationshipManager('satisfying_actions', () => {
      return new RelationshipManager(this, 'satisfying_actions', 'many', Satisfaction as typeof Relationship);
    });
  }

  // Relationship manager for 'derived_requirements' - Python ORM style (cached)
  get derived_requirements(): RelationshipManager<this, RequirementDefinition> {
    return this.getOrCreateRelationshipManager('derived_requirements', () => {
      return new RelationshipManager(this, 'derived_requirements', 'many', Derivation as typeof Relationship);
    });
  }

  // Relationship manager for 'source_requirement' - Python ORM style (cached)
  get source_requirement(): RelationshipManager<this, RequirementDefinition> {
    return this.getOrCreateRelationshipManager('source_requirement', () => {
      return new RelationshipManager(this, 'source_requirement', 'one', Derivation as typeof Relationship);
    });
  }

  // Relationship manager for 'refined_requirements' - Python ORM style (cached)
  get refined_requirements(): RelationshipManager<this, RequirementDefinition> {
    return this.getOrCreateRelationshipManager('refined_requirements', () => {
      return new RelationshipManager(this, 'refined_requirements', 'many', Refinement as typeof Relationship);
    });
  }

  // Relationship manager for 'sub_requirements' - Python ORM style (cached)
  get sub_requirements(): RelationshipManager<this, RequirementDefinition> {
    return this.getOrCreateRelationshipManager('sub_requirements', () => {
      return new RelationshipManager(this, 'sub_requirements', 'many', HasSubRequirements as typeof Relationship);
    });
  }

  // Relationship manager for 'parent_requirement' - Python ORM style (cached)
  get parent_requirement(): RelationshipManager<this, RequirementDefinition> {
    return this.getOrCreateRelationshipManager('parent_requirement', () => {
      return new RelationshipManager(this, 'parent_requirement', 'one', HasSubRequirements as typeof Relationship);
    });
  }

  // Relationship manager for 'owner' - Python ORM style (cached)
  get owner(): RelationshipManager<this, PDMProduct> {
    return this.getOrCreateRelationshipManager('owner', () => {
      return new RelationshipManager(this, 'owner', 'one', HasRequirements as typeof Relationship);
    });
  }

}
